import { createRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { rootRoute } from '@/routes/root'

import { hasRecentAuth0LoginAttempt, isAuth0LoginEnabled } from './domain/auth0'
import { resetPasswordRoute } from './view/reset/route'
import { signupInvitedRoute } from './view/signup/route.tsx'
import { AppSkeleton } from '../../fragments/appSkeleton'

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: ({ location }) => {
    // A recent attempt means Auth0 just bounced the visitor back here, so falling through to the
    // v1 form breaks the redirect loop instead of handing them to Auth0 again
    if (!isAuth0LoginEnabled() || hasRecentAuth0LoginAttempt()) return

    throw redirect({
      to: '/loginv2',
      search: { redirect: (location.search as { redirect?: string }).redirect ?? '/' },
      replace: true,
    })
  },
}).lazy(() => import('./view/login/login').then((d) => d.Route))

/**
 * Hard bypass for the /login -> /loginv2 redirect
 */
export const loginv1Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loginv1',
}).lazy(() => import('./view/login/login').then((d) => d.RouteV1))

export const loginv2Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loginv2',
  validateSearch: z.object({
    redirect: z
      .string()
      .nullish()
      .transform((data) => data ?? '/'),
    organization: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    connection: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    user: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    email: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
  }),
}).lazy(() => import('./view/loginv2/loginv2.tsx').then((d) => d.Route))

export const loginv2CallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/loginv2/callback',
}).lazy(() => import('./view/loginv2/loginv2Callback.tsx').then((d) => d.Route))

export const shipV1LoginAsRedirect = createRoute({
  path: '/loginas',
  getParentRoute: () => rootRoute,
  validateSearch: z.object({ redirectTo: z.string().catch('/') }),
  loaderDeps: ({ search: { redirectTo } }) => ({ redirectTo }),
  loader: ({ deps: { redirectTo } }) => {
    throw redirect({ to: '/login-as', search: { redirect: redirectTo } })
  },
})

export const loginAsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login-as',
  validateSearch: z.object({ redirect: z.string().catch('/') }),
  pendingComponent: AppSkeleton,
}).lazy(() => import('./view/loginAs/loginAs').then((d) => d.Route))

export { resetPasswordRoute, signupInvitedRoute }
